import { Directive, ElementRef, HostListener, Input, Optional } from '@angular/core';
import { NgControl } from '@angular/forms';

@Directive({
    selector: '[appIntegerOnly]',
    standalone: false
})
export class IntegerOnlyDirective {
  // Lower bound. Undefined (the default) means unbounded -- preserves this directive's
  // original behaviour (freely signed integers) for every caller that doesn't explicitly
  // opt in to a floor. Only a caller that binds [min] gets restricted input -- e.g. the
  // Find Phrases "Variants" field must never accept a negative value (see
  // extractor-transform-detail.component.html's endOffset input): a negative Variants
  // count silently collapses the rule's output to a single value in the engine
  // (_variants_filter in Extractor_Engine.py), which the field's own tooltip never
  // documents as meaningful -- restricting it here removes that confusion at the source
  // rather than relying on the Analyst to never type it.
  @Input() min: number | undefined = undefined;
  private timeout: any; // Store the timeout reference
  private regex: RegExp = new RegExp(/^-?\d*$/);

  constructor(private el: ElementRef, @Optional() private control: NgControl) { }

  private get hasFloor(): boolean {
    return this.min !== undefined && this.min !== null;
  }

  private get minusAllowed(): boolean {
    return !this.hasFloor || (this.min as number) < 0;
  }

  @HostListener('input', ['$event'])
  onInputChange(event: InputEvent): void {
    const inputValue = this.el.nativeElement.value;
    // Remove any non-integer characters -- drop the minus sign entirely when this
    // field has a non-negative floor, so a pasted "-5" can't slip past the keypress
    // guard below.
    const sanitizedValue = inputValue.replace(this.minusAllowed ? /[^0-9-]/g : /[^0-9]/g, '');
    let normalizedValue=sanitizedValue;
    // Validate if the sanitized value matches the number format
    if (sanitizedValue && !this.regex.test(sanitizedValue)) {
      normalizedValue = sanitizedValue.slice(0, -1); // Remove last invalid character
    } else {
      // If valid, normalize the number format (e.g., -09 to -9)
      normalizedValue = sanitizedValue.replace(/^(-?)0+(\d)/, '$1$2');
    }

    // Clear previous timeout to avoid multiple updates
    clearTimeout(this.timeout);
    // Set a timer to update the input value
    this.timeout = setTimeout(() => {
      if (this.el.nativeElement.value !== normalizedValue) {
        this.el.nativeElement.value = normalizedValue;
        event.stopPropagation();
        // Trigger Angular's change detection manually
        //this.el.nativeElement.value.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }, 300); // Adjust the delay as needed (300 milliseconds in this case)

  }

  @HostListener('blur')
  onInputBlur(): void {
    const input = this.el.nativeElement as HTMLInputElement;
    const resetValue = this.hasFloor ? (this.min as number) : 0;
    // Set to the floor (or 0 when unbounded) if input is empty or just a lone '-'
    if (input.value === '' || input.value === '-' || input.value === '-0') {
      input.value = resetValue.toString();
      if (this.control && this.control.control) {
        this.control.control.setValue(resetValue);  // Update the model
      }
      return;
    }
    // Clamp anything that still slipped below an explicit floor (e.g. a value the
    // model already held before this field's floor was set dynamically).
    if (this.hasFloor) {
      const numeric = parseInt(input.value, 10);
      if (!isNaN(numeric) && numeric < (this.min as number)) {
        input.value = (this.min as number).toString();
        if (this.control && this.control.control) {
          this.control.control.setValue(this.min);
        }
      }
    }
  }
  @HostListener('keypress', ['$event'])
  onKeyPress(event: KeyboardEvent) {
    const key = event.key;
    const minusAllowedHere = this.minusAllowed && (event.target as HTMLInputElement).value.length === 0;

    // Allow digits (0-9), and a single leading minus sign only when this field has
    // no floor or a negative one.
    if (!/^\d$/.test(key) && !(key === '-' && minusAllowedHere)) {
      event.preventDefault();
    }
  }
}
